import './actions/ActionsPanel.css';
import './PendingMembershipsPanel.css';

import { useState } from 'react';
import { Clock } from 'lucide-react';
import { usePendingMembershipRequests } from '../queries';
import type { MembershipRequestRow } from '../api/membership.api';
import ActivateMembershipModal from './ActivateMembershipModal';

const NULL_SYMBOL = '—';

const initials = (name: string | null | undefined): string => {
  const safe = String(name || '').trim();
  if (!safe) return '?';
  const parts = safe.split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || '').join('') || safe[0]!.toUpperCase();
};

const formatRequested = (iso: string | null | undefined): string => {
  if (!iso) return NULL_SYMBOL;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return NULL_SYMBOL;
  const minutes = Math.floor((Date.now() - parsed.getTime()) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return parsed.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
};

const formatExpiresIn = (iso: string | null | undefined): string => {
  if (!iso) return NULL_SYMBOL;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return NULL_SYMBOL;
  const ms = parsed.getTime() - Date.now();
  if (ms <= 0) return 'expired';
  const minutes = Math.floor(ms / 60_000);
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return hours > 0 ? `expires in ${hours}h ${mins}m` : `expires in ${mins}m`;
};

const PendingMembershipsPanel = () => {
  const query = usePendingMembershipRequests(true);
  const rows = query.data ?? [];
  const [activeRequest, setActiveRequest] = useState<MembershipRequestRow | null>(null);

  return (
    <article className="actions-panel" data-testid="pending-memberships-panel">
      <header className="actions-panel__header">
        <span className="actions-panel__icon" aria-hidden="true">
          <Clock size={16} />
        </span>
        <div className="actions-panel__title-block">
          <h3 className="actions-panel__title">Pending memberships</h3>
          <p className="actions-panel__subtitle">
            Customers started these in the app. Ask for their 6-digit code to activate.
          </p>
        </div>
        {rows.length > 0 ? <span className="actions-panel__suffix"><strong>{rows.length}</strong> waiting</span> : null}
      </header>

      {query.isLoading ? (
        <div className="actions-panel__loading">Loading…</div>
      ) : query.isError ? (
        <div className="actions-panel__empty">Couldn’t load pending requests. Try again.</div>
      ) : rows.length === 0 ? (
        <div className="actions-panel__empty">No pending membership requests right now.</div>
      ) : (
        <ul className="actions-panel__list">
          {rows.map((row, index) => (
            <li
              key={row.requestUuid || index}
              className="actions-panel__item"
              data-testid={`pending-membership-${index}`}
            >
              <span className="actions-panel__avatar" aria-hidden="true">
                {initials(row.customerName)}
              </span>
              <div className="actions-panel__item-body">
                <div className="actions-panel__item-line">
                  <span className="actions-panel__item-name">{row.customerName || NULL_SYMBOL}</span>
                  {row.customerPhoneMasked ? (
                    <span className="actions-panel__item-sub"> · {row.customerPhoneMasked}</span>
                  ) : null}
                </div>
                <div className="actions-panel__item-sub">
                  {row.planName || NULL_SYMBOL} · requested {formatRequested(row.createdAt)}
                </div>
              </div>
              <span className="actions-panel__time">{formatExpiresIn(row.expiresAt)}</span>
              <button
                type="button"
                className="pending-activate-btn"
                onClick={() => setActiveRequest(row)}
                data-testid={`pending-activate-${index}`}
              >
                Activate
              </button>
            </li>
          ))}
        </ul>
      )}

      <ActivateMembershipModal
        open={activeRequest != null}
        request={activeRequest}
        onClose={() => setActiveRequest(null)}
      />
    </article>
  );
};

export default PendingMembershipsPanel;
